// src/constants/marketSchedule.js
//
// The structured schedule behind the market info panel: for each market x
// product, when the auction closes, when WE publish, and which delivery
// periods that auction actually clears.
//
// WHY STRUCTURED AND NOT PROSE. publicationTimes.js already carries the
// one-line caption under each panel header and stays exactly as it is. This
// module holds the same facts as DATA so the panel can do three things a
// sentence cannot: render every time in either timezone, compute the margin
// between our run and the gate for the delivery day actually selected (DST
// included), and state which periods an auction covers.
//
// TWO TIMEZONES, AND WHICH ONE IS NATIVE.
// The coupled auctions are defined in CET by SDAC/SIDC - EPEX SPOT states the
// day-ahead order book closing at 12:00 CET and the intraday auctions at 15:00
// / 22:00 CET (D-1) and 10:00 CET (D). The balancing gate is defined in EET by
// ADMIE: "16:45 EET on calendar day D-1". So there is no single correct zone.
// Each entry declares the zone its gate is WRITTEN IN (`nativeTz`), the panel
// shows both, and the native one is marked - a converted number is never
// presented as if it were the source's own.

export const CET_TZ = "Europe/Paris";

export const MARKET_TZ = {
  GR: "Europe/Athens",
  FR: "Europe/Paris",
  DE: "Europe/Berlin",
  NL: "Europe/Amsterdam",
  HU: "Europe/Budapest",
};

// ---------------------------------------------------------------------------
// Timezone arithmetic. Intl only - no library, and correct across DST because
// the offset is read for the specific instant rather than assumed.
// ---------------------------------------------------------------------------

function offsetMinutes(timeZone, date) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = {};
  dtf.formatToParts(date).forEach((p) => {
    parts[p.type] = p.value;
  });
  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second)
  );
  return (asUTC - date.getTime()) / 60000;
}

/** The instant at which `hh:mm` occurs on `y-m-d` in `timeZone`.
 *  Two passes: the first offset is read at the naive guess, the second at the
 *  corrected instant, which is what makes a DST-transition day come out right. */
export function zonedWallTimeToInstant(timeZone, y, m, d, hh, mm) {
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  let off = offsetMinutes(timeZone, new Date(guess));
  const once = new Date(guess - off * 60000);
  off = offsetMinutes(timeZone, once);
  return new Date(guess - off * 60000);
}

/** `HH:MM` for an instant, rendered in `timeZone`. */
export function formatInZone(instant, timeZone) {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  }).format(instant);
}

/** The short zone name (CET/CEST, EET/EEST) for an instant. */
export function zoneAbbrev(instant, timeZone) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    timeZoneName: "short",
  }).formatToParts(instant);
  const found = parts.find((p) => p.type === "timeZoneName");
  return found ? found.value : timeZone;
}

/** "4 h 15 m" between two instants; null when either is missing. */
export function formatGap(fromInstant, toInstant) {
  if (!fromInstant || !toInstant) return null;
  const mins = Math.round((toInstant.getTime() - fromInstant.getTime()) / 60000);
  if (mins <= 0) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (!h) return `${m} m`;
  if (!m) return `${h} h`;
  return `${h} h ${m} m`;
}

function shiftDate(dateKey, days) {
  const [y, m, d] = dateKey.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return [dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate()];
}

/** Resolve one {hour, minute, tz, dayOffset} spec to an instant, for the
 *  delivery day `deliveryDateKey` ("YYYY-MM-DD"). dayOffset -1 means D-1. */
export function resolveEvent(spec, deliveryDateKey) {
  if (!spec || !deliveryDateKey) return null;
  const [y, m, d] = shiftDate(deliveryDateKey, spec.dayOffset || 0);
  return zonedWallTimeToInstant(spec.tz, y, m, d, spec.hour, spec.minute);
}

// ---------------------------------------------------------------------------
// The schedule itself.
// ---------------------------------------------------------------------------
//
// gateKind:
//   "market"   - the exchange's or the TSO's own published deadline
//   "internal" - OUR vintage gate, not a market deadline. XBID is continuous:
//                each contract closes at its own delivery start, so there is
//                no single closing instant. Calling our own cut-off a market
//                gate would be a fiction.

const GR = {
  dam: {
    title: "Day-ahead auction (DAM)",
    gate: { hour: 12, minute: 0, tz: CET_TZ, dayOffset: -1 },
    gateKind: "market",
    nativeTz: CET_TZ,
    authority: "SDAC — single day-ahead coupling",
    forecast: { hour: 8, minute: 45, tz: MARKET_TZ.GR, dayOffset: -1 },
    forecastNote: "Runs after the 08:30 ENTSO-E load and RES forecasts for D land.",
    periods: "Every quarter-hour of the CET delivery day.",
    results:
      "Results from ~12:55 CET (D−1); ingested ~14:00 Europe/Athens from the HEnEx EL-DAM workbook.",
    caveats: [
      "The HEnEx workbook is a CET day, so an Athens delivery day is stitched from two of them: 4 quarter-hours from the previous auction and 92 from its own. Those first four are already cleared when the 08:45 run forecasts them.",
    ],
  },
  ida1: {
    title: "Intraday auction 1 (IDA1)",
    gate: { hour: 15, minute: 0, tz: CET_TZ, dayOffset: -1 },
    gateKind: "market",
    nativeTz: CET_TZ,
    authority: "SIDC — pan-European single intraday coupling",
    forecast: { hour: 14, minute: 15, tz: MARKET_TZ.GR, dayOffset: -1 },
    periods: "All delivery periods of the delivery day.",
    results: "Ingested ~16:00 Europe/Athens via HEnEx.",
  },
  ida2: {
    title: "Intraday auction 2 (IDA2)",
    gate: { hour: 22, minute: 0, tz: CET_TZ, dayOffset: -1 },
    gateKind: "market",
    nativeTz: CET_TZ,
    authority: "SIDC — pan-European single intraday coupling",
    forecast: { hour: 17, minute: 0, tz: MARKET_TZ.GR, dayOffset: -1 },
    periods: "All delivery periods of the delivery day.",
    results: "Ingested ~23:00 Europe/Athens via HEnEx.",
  },
  ida3: {
    title: "Intraday auction 3 (IDA3)",
    gate: { hour: 10, minute: 0, tz: CET_TZ, dayOffset: 0 },
    gateKind: "market",
    nativeTz: CET_TZ,
    authority: "SIDC — pan-European single intraday coupling",
    forecast: { hour: 9, minute: 0, tz: MARKET_TZ.GR, dayOffset: 0 },
    periods:
      "Only 12:00–24:00 CET of the delivery day — 48 quarter-hours, not 96. The morning is not traded in this auction.",
    periodsEmphasis: true,
    results: "Ingested ~11:00 Europe/Athens via HEnEx.",
    caveats: [
      "The only run that happens inside the delivery day itself.",
    ],
  },
  balancing: {
    title: "Balancing — reserve capacity and imbalance",
    gate: { hour: 16, minute: 45, tz: MARKET_TZ.GR, dayOffset: -1 },
    gateKind: "market",
    nativeTz: MARKET_TZ.GR,
    authority: "ADMIE — Integrated Scheduling Process, ISP1",
    forecast: { hour: 15, minute: 30, tz: MARKET_TZ.GR, dayOffset: -1 },
    forecastNote:
      "Seven independent series: FCR, aFRR and mFRR up and down, plus the imbalance price.",
    periods: "Every quarter-hour of the delivery day.",
    results:
      "Reserve capacity from ENTSO-E on the D−1 evening. Imbalance arrives in weekly batches, typically 4–11 days after delivery.",
    caveats: [
      "Four of the seven series publish a rolling-median baseline rather than the model, because the backtest says the baseline wins.",
    ],
  },
  xbid: {
    title: "Continuous intraday (XBID)",
    gate: { hour: 0, minute: 0, tz: MARKET_TZ.GR, dayOffset: 0 },
    gateKind: "internal",
    nativeTz: MARKET_TZ.GR,
    authority: "Continuous trading — each contract closes at its own delivery start",
    forecast: { hour: 23, minute: 20, tz: MARKET_TZ.GR, dayOffset: -1 },
    forecastNote:
      "Late rung. Waits for the 22:00 CET IDA2 gate so it can publish off the freshest cleared anchor.",
    periods: "Per-MTU session VWAP for the delivery day.",
    results:
      "Partial workbook during the delivery day; the final version ~09:25 Europe/Athens on D+1, which is when scores appear.",
    caveats: [
      "The cut-off shown is OUR vintage gate, not a market deadline — continuous trading has no single closing instant.",
      "An early rung at 17:15 Europe/Athens is built and measured but not switched on.",
    ],
  },
};

export const MARKET_SCHEDULE = { GR };

export function getMarketSchedule(market, productKey) {
  const byMarket = MARKET_SCHEDULE[market];
  if (!byMarket) return null;
  return byMarket[productKey] || null;
}

export function getMarketProducts(market) {
  const byMarket = MARKET_SCHEDULE[market];
  if (!byMarket) return [];
  return Object.keys(byMarket).map((key) => ({ key, ...byMarket[key] }));
}
